import b from 'b_';
import {
    addMonths,
    eachDayOfInterval,
    isSameDay,
    isSameMonth,
    isSameWeek,
    isWeekend,
    lastDayOfMonth,
    lastDayOfWeek,
    startOfMonth,
    startOfWeek,
} from 'date-fns';
import { ArrowShortBold } from 'education-icons';
import chunk from 'lodash/chunk';
import { Button, SimpleCallback, Spin, Text } from 'platform-components';
import { UI_KIT_TEXT_TYPOGRAPHY } from 'platform-components/src/components/constants';
import React from 'react';
import { accessibleClick } from '@edu-frontend/modules/src/shared/lib/a11y';

import { formatDate } from 'utils/date/formatDate';

import './week-picker__month.scss';

interface MarkedDay {
    day: Date;
    color: string;
}

interface Props {
    selectedWeek: Date;
    onChangeWeek: (startDate: Date, endDate: Date) => void;
    selectedMonth: Date;
    onChangeMonth: (date: Date) => void;
    markedDays?: MarkedDay[];
    onMonthClick: SimpleCallback;
    minDate?: Date;
    progress?: boolean;
}

export const MonthView = (props: Props) => {
    const { selectedWeek, onChangeWeek, markedDays, onMonthClick, selectedMonth, onChangeMonth, minDate, progress } =
        props;
    const days = getDays(selectedMonth);
    const weeks = chunk(days, 7);

    const handlePrevMonthClick = () => {
        onChangeMonth(addMonths(selectedMonth, -1));
    };

    const handleNextMonthClick = () => {
        onChangeMonth(addMonths(selectedMonth, 1));
    };

    const handleSelectWeek = (week: Date[]) => {
        onChangeWeek(week[0], week[week.length - 1]);
    };

    return (
        <>
            <div className="week-picker__controls">
                <Button
                    className="week-picker__prev-month"
                    disabled={minDate && isSameMonth(selectedMonth, minDate)}
                    icon={<ArrowShortBold />}
                    onClick={handlePrevMonthClick}
                    view={Button.VIEW.CLEAR}
                />
                <Button className="week-picker__current-month" onClick={onMonthClick} view={Button.VIEW.CLEAR}>
                    {formatDate(selectedMonth, 'LLLL yyyy')}
                </Button>
                <Button
                    className="week-picker__next-month"
                    icon={<ArrowShortBold />}
                    onClick={handleNextMonthClick}
                    view={Button.VIEW.CLEAR}
                />
            </div>
            <div className="week-picker__week-days">
                {weeks[0].map((date) => (
                    <div className="week-picker__week-day">
                        <Text typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphSRegular}>{formatDate(date, 'eeeeee')}</Text>
                    </div>
                ))}
            </div>
            <div className={b('week-picker__days', { progress })}>
                {weeks.map((week, weekIndex) => (
                    <div
                        className={b('week-picker', 'week', {
                            selected: isSameWeek(selectedWeek, week[0]),
                        })}
                        key={weekIndex}
                        {...accessibleClick(() => handleSelectWeek(week))}
                    >
                        {week.map((date, dateIndex) => {
                            const mark = progress ? null : markedDays?.find(({ day }) => isSameDay(day, date));
                            return (
                                <div
                                    className={b('week-picker', 'day', {
                                        weekend: isWeekend(date),
                                        'past-or-future': !isSameMonth(selectedMonth, date),
                                    })}
                                    key={dateIndex}
                                    style={mark ? { background: mark.color } : {}}
                                >
                                    <Text typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphMRegular}>
                                        {formatDate(date, 'd')}
                                    </Text>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
            {progress && <Spin mix="week-picker__loader" size={Spin.SIZE.XS} />}
        </>
    );
};

function getDays(date: Date) {
    const firstDay = startOfWeek(startOfMonth(date), { weekStartsOn: 1 });

    return eachDayOfInterval({
        start: firstDay,
        end: lastDayOfWeek(lastDayOfMonth(date), { weekStartsOn: 1 }),
    });
}
